import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton
} from "@material-ui/core";
import { withRouter, Link as RouterLink } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { remCalc, getUrlParams } from "../../utils/helpers";
import API, { API_NO_TOKEN } from "../../utils/axios";
import { DWA_API_URL } from "../data/constants";
import LoadingBar from "./LoadingBar";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import FullSreenDialog from "./FullSreenDialog";
import { login } from "../../store/actions/auth";
import { useSnackbar } from "notistack";
import OptimizedTooltip from "../common/OptimizedTooltip";

const useStyles = makeStyles(theme => ({
  textField: {
    width: "100%",
    marginTop: theme.spacing(1),
    "&:first-of-type": {
      marginTop: theme.spacing(3)
    }
  },
  signinWrapper: {
    boxSizing: "border-box",
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(6, 5, 5),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",

    backgroundColor: "#fff",
    boxShadow: "0px 0.5px 8px rgba(38, 38, 38, 0.16);",
    height: remCalc(436)
  },
  signinHeader: {
    lineHeight: 1,
    marginBottom: theme.spacing(1),
    fontWeight: 500
  },
  signinText: {
    lineHeight: 1.5
  },
  link: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    cursor: "pointer",
    lineHeight: "1.5rem",
    "&:hover": {
      textDecoration: "underline"
    }
  },
  button: {
    textTransform: "none",
    boxShadow: "none !important",
    minWidth: theme.spacing(10),
    fontSize: remCalc(15),
    lineHeight: 1.6
  },
  buttonSimple: {
    textTransform: "none",
    marginLeft: theme.spacing(-2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    fontSize: "0.875rem"
  },
  labelTextShrinked: {
    transform: "translate(0, 2.5px) scale(0.6875)"
  },
  helperText: {
    margin: theme.spacing(0.5, 0),
    fontSize: "0.6875rem"
  },
  passwordHint: {
    fontSize: "0.75rem",
    lineHeight: "0.875rem",
    textAlign: "left"
  },
  helperTextAuth: {
    margin: 0
  }
}));

function NewPassword({ history, login }) {
  const classes = useStyles();

  const [inputValues, setInputValues] = useState({
    password: ""
  });

  const recover_code = useRef();
  const isDriver = useRef();

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    recover_code.current = getUrlParams("recover_code");
    isDriver.current = getUrlParams("driver");
    console.log(recover_code, isDriver);

    if (!recover_code.current) history.replace("/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [errorValues, setErrorValues] = useState({
    password: ""
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = e => {
    e.preventDefault();
    if (inputValues.password.length >= 6) {
      setErrorValues({ password: "" });
      setLoading(true);
      if (isDriver.current) {
        API_NO_TOKEN.put("/v1/drivers/password/recover", {
          password: inputValues.password,
          recover_token: recover_code.current
        })
          .then(res => {
            setLoading(false);
            API.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${res.data.accessToken}`;

            setInputValues({
              ...inputValues,
              password: ""
            });

            window.location.href = DWA_API_URL;
          })
          .catch(err => {
            console.dir(err);
            if (err && err.response && err.response.status === 404) {
              enqueueSnackbar(
                "Recovery session expired, please try to reset password again."
              );
            }
            setLoading(false);
          })
          .finally(() => {});
      } else {
        API_NO_TOKEN.put("/v1/users/password/recover", {
          password: inputValues.password,
          recover_token: recover_code.current
        })
          .then(res => {
            setLoading(false);
            // Sign in after successfully updating password
            API.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${res.data.accessToken}`;
            login(
              res.data.accessToken,
              res.data.expiresAt,
              res.data.refreshToken,
              true
            );
            history.replace("/");
          })
          .catch(err => {
            console.dir(err);
            if (err && err.response && err.response.status === 404) {
              enqueueSnackbar(
                "Recovery session expired, please try to reset password again."
              );
            }
            setLoading(false);
          })
          .finally(() => {});
      }
    } else {
      if (!inputValues.password.length) {
        setErrorValues({
          ...errorValues,
          password: "Enter a password"
        });
      } else {
        setErrorValues({
          ...errorValues,
          password: "Use 6 characters or more "
        });
      }
    }
  };

  const handleChange = e => {
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
  };

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };
  return (
    <FullSreenDialog>
      <Box
        width={remCalc(408)}
        minHeight={remCalc(436)}
        className={classes.signinWrapper}
        component={"form"}
        display="flex"
        flexDirection="column"
        noValidate
        mb={15}
        onSubmit={handleSubmit}
      >
        {loading && <LoadingBar />}
        {/* Header */}
        <Box>
          <Typography variant="h5" className={classes.signinHeader}>
            Enter password
          </Typography>
          <Typography variant="body1" className={classes.signinText}>
            Please enter a new password for <br />
            your account
          </Typography>
        </Box>

        {/* Inputs */}
        <Box>
          <TextField
            name="password"
            type={showPassword ? "text" : "password"}
            value={inputValues.password}
            onChange={handleChange}
            label="Password"
            className={classes.textField}
            helperText={errorValues.password || " "}
            error={!!errorValues.password}
            required
            InputLabelProps={{
              required: false,
              classes: { shrink: classes.labelTextShrinked }
            }}
            FormHelperTextProps={{ classes: { root: classes.helperText } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <OptimizedTooltip
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? (
                        <Visibility />
                      ) : (
                        <VisibilityOff style={{ color: "#DADADA" }} />
                      )}
                    </IconButton>
                  </OptimizedTooltip>
                </InputAdornment>
              )
            }}
          />
          <Typography
            variant={"body2"}
            className={classes.passwordHint}
            style={{ marginTop: errorValues.password ? "1rem" : 0 }}
          >
            Choose a password that is at least 6 characters long.
          </Typography>
        </Box>

        {/* Actions */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={"auto"}
        >
          <Button
            component={RouterLink}
            to="/login"
            color="primary"
            className={classes.buttonSimple}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            type="submit"
          >
            Submit
          </Button>
        </Box>
      </Box>
    </FullSreenDialog>
  );
}

const mapStateToProps = state => ({});

export default connect(mapStateToProps, { login })(withRouter(NewPassword));
